// btw-front/src/pages/AdminDashboard.jsx
// 관리자 대시보드 페이지 셸 — 헤더·메인 공지 배너·탭 바·탭 라우팅(?tab=).
// 각 탭의 내용은 components/admin/dashboard/*Tab.jsx 로 분리.
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import * as notices from "../api/notices";
import * as adminDealReviews from "../api/adminDealReviews";
import { REVIEW_PENDING } from "../constants/dealReview";
import DealsTab from "../components/admin/dashboard/DealsTab";
import DealReviewsTab from "../components/admin/dashboard/DealReviewsTab";
import SignupsTab from "../components/admin/dashboard/SignupsTab";
import PerformanceTab from "../components/admin/dashboard/PerformanceTab";
import DeletedDealsTab from "../components/admin/dashboard/DeletedDealsTab";
import PartnersTab from "../components/admin/dashboard/PartnersTab";
import StaleDealsTab from "../components/admin/dashboard/StaleDealsTab";
import NoticesTab from "../components/admin/dashboard/NoticesTab";
import StatsTab from "../components/admin/dashboard/StatsTab";

export default function AdminDashboard() {
  const { t } = useTranslation();
  // 활성 탭을 URL(?tab=)에 반영 — 상세 페이지에서 돌아올 때(뒤로가기/목록으로) 탭이 유지됨.
  const [searchParams, setSearchParams] = useSearchParams();
  // [계정] 탭은 [파트너] 탭으로 흡수됨 — 기존 ?tab=accounts 링크·북마크는 파트너로 보낸다.
  const rawTab = searchParams.get("tab") || "deals";
  const tab = rawTab === "accounts" ? "partners" : rawTab;
  // 탭을 바꾸면 이전 탭의 필터 쿼리(status·q·sort)는 버린다. 같은 탭 재클릭은 무시 —
  // 쿼리만 지워지면 화면의 필터와 URL 이 어긋나 상세에서 돌아올 때 필터가 사라진다.
  const setTab = (k) => {
    if (k === tab) return;
    setSearchParams(k === "deals" ? {} : { tab: k }, { replace: true });
  };
  const navigate = useNavigate();

  // 심사 대기 건수 — [딜 심사] 탭 라벨에 배지로 표시. 관리자가 대시보드를 열자마자
  // 처리할 일이 있는지 보이게 한다(탭을 눌러야 알게 되면 심사가 밀린다).
  // 탭 안에서 승인·반려하면 이 숫자도 갱신되어야 하므로 탭이 콜백으로 알려준다.
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    adminDealReviews
      .list(REVIEW_PENDING)
      .then((rows) => {
        if (!cancelled) setPendingReviewCount(rows.length);
      })
      .catch(() => {
        if (!cancelled) setPendingReviewCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 대시보드 메인 공지 1건 — 파트너 대시보드와 동일하게 최상단 배너로 표시(모든 탭 공통). 없으면 미표시.
  const [featuredNotice, setFeaturedNotice] = useState(null);
  useEffect(() => {
    let cancelled = false;
    notices
      .featured()
      .then((n) => {
        if (!cancelled) setFeaturedNotice(n || null);
      })
      .catch(() => {
        if (!cancelled) setFeaturedNotice(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">{t("admin.title")}</h1>
          </header>

          {/* 메인 공지 — [공지사항] 탭에서 '대시보드 메인 공지'로 지정한 1건. 파트너 대시보드 배너와 동일 UI. */}
          {featuredNotice && (
            <button
              type="button"
              onClick={() => navigate(`/partners/notices/${featuredNotice.id}`)}
              className="mt-6 flex w-full items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 px-4 py-3 text-left transition hover:bg-accent/10"
            >
              <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-white">
                {t("admin.featured.tag")}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                {featuredNotice.title}
              </span>
              <span className="shrink-0 text-xs font-medium text-accent">
                {t("admin.featured.view")}
              </span>
            </button>
          )}

          {/* 탭 순서 — 딜 관련을 앞에 몰아 둔다: 확정된 딜([딜 관리]) → 심사 대기([딜 심사]) →
              정체([관리 대상 딜]) → 삭제([삭제된 딜]). 딜의 생애를 따라가는 순서라 관리자가
              탭을 찾지 않고 흐름대로 읽는다. 그 뒤는 파트너·운영 영역. */}
          <div className="mt-6 flex flex-wrap gap-1 border-b border-slate-200">
            <TabBtn current={tab} k="deals" onClick={setTab} label={t("admin.tabs.deals")} />
            <TabBtn
              current={tab}
              k="reviews"
              onClick={setTab}
              label={t("admin.tabs.reviews")}
              count={pendingReviewCount}
            />
            <TabBtn current={tab} k="stale" onClick={setTab} label={t("admin.tabs.stale")} />
            <TabBtn current={tab} k="deleted" onClick={setTab} label={t("admin.tabs.deleted")} />
            <TabBtn current={tab} k="signups" onClick={setTab} label={t("admin.tabs.signups")} />
            <TabBtn current={tab} k="partners" onClick={setTab} label={t("admin.tabs.partners")} />
            <TabBtn
              current={tab}
              k="performance"
              onClick={setTab}
              label={t("admin.tabs.performance")}
            />
            <TabBtn current={tab} k="notices" onClick={setTab} label={t("admin.tabs.notices")} />
            <TabBtn current={tab} k="stats" onClick={setTab} label={t("admin.tabs.stats")} />
          </div>

          {/* 렌더 순서는 화면에 영향이 없지만 탭 바와 같은 순서로 둬 대조하기 쉽게 한다 */}
          <div className="mt-6">
            {tab === "deals" && <DealsTab />}
            {tab === "reviews" && <DealReviewsTab onPendingCountChange={setPendingReviewCount} />}
            {tab === "stale" && <StaleDealsTab />}
            {tab === "deleted" && <DeletedDealsTab />}
            {tab === "signups" && <SignupsTab />}
            {tab === "partners" && <PartnersTab />}
            {tab === "performance" && <PerformanceTab />}
            {tab === "notices" && <NoticesTab />}
            {tab === "stats" && <StatsTab />}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

// count — 0 보다 크면 라벨 옆에 건수 배지. 처리 대기가 있는 탭을 눌러보지 않아도 알 수 있게 한다.
function TabBtn({ current, k, onClick, label, count = 0 }) {
  const active = current === k;
  return (
    <button
      type="button"
      onClick={() => onClick(k)}
      className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition ${
        active
          ? "border-accent text-accent"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {label}
      {count > 0 && (
        <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700">
          {count}
        </span>
      )}
    </button>
  );
}
